import React, { useState, useEffect } from 'react';
import { auth } from '../../../../../firebase/config';
import Modal from '../../../../../components/Modal';
import TextField from '../../../../../components/TextField';
import Button from '../../../../../components/Button';
import { FiSearch, FiAlertTriangle } from 'react-icons/fi';
import Icon from '../../../../../components/Icon';
import { updateDeploymentData, replaceDeploymentData, fetchAvailableBranches, fetchAvailableCompanies } from '../service/editDeploymentService';
import type { Branch, Deployment } from '../../../service/deploymentsService';
import type { Company } from '../../../../companies/service/companiesService';
import { fetchDeploymentBankId } from '../../../service/deploymentsService';
import Toast from '../../../../../components/Toast';

type EditDeploymentModalProps = {
    isOpen: boolean;
    onClose: () => void;
    deployment: Deployment | null;
    onSuccess: () => void;
};

function EditDeploymentModal({ isOpen, onClose, deployment, onSuccess }: EditDeploymentModalProps) {
    const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
    const [availableCompanies, setAvailableCompanies] = useState<Company[]>([]);
    const [bankId, setBankId] = useState<string | null>(null);
    
    // Modal states
    const [showCompanyModal, setShowCompanyModal] = useState(false);
    const [showConflictModal, setShowConflictModal] = useState(false);
    const [conflictData, setConflictData] = useState<{ existingDeployment: any; existingCompany: Company } | null>(null);
    
    // Search states
    const [companySearchTerm, setCompanySearchTerm] = useState('');
    
    // UI states
    const [showToast, setShowToast] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const uid = auth.currentUser?.uid;
    const userRole = sessionStorage.getItem('role');
    const isBankUser = userRole === 'bank';

    // Initialize data when modal opens
    useEffect(() => {
        if (isOpen && deployment && uid) {
            const fetchData = async () => {
                setLoading(true);
                try {
                    // Set selected company from deployment
                    if (deployment.company) {
                        setSelectedCompany(deployment.company);
                    }

                    // Fetch bankId
                    const fetchedBankId = await fetchDeploymentBankId(uid, isBankUser);
                    if (fetchedBankId) {
                        setBankId(fetchedBankId);
                    }

                    // Fetch available companies
                    const companies = await fetchAvailableCompanies(uid, isBankUser);
                    setAvailableCompanies(companies);
                } catch (err) {
                    console.error('Error fetching data:', err);
                    showToastMessage('Failed to load data', 'error');
                } finally {
                    setLoading(false);
                }
            };
            fetchData();
        }
    }, [isOpen, deployment, uid, isBankUser]);

    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            setSelectedCompany(null);
            setCompanySearchTerm('');
            setShowCompanyModal(false);
            setShowConflictModal(false);
            setConflictData(null);
        }
    }, [isOpen]);

    const showToastMessage = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') => {
        setSuccessMessage(message);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
    };

    // Filter companies - show only same type when editing
    const filteredCompanies = availableCompanies.filter(company => {
        const companyName = company.companyName || '';
        const companyId = company.companyId || '';
        const searchTerm = companySearchTerm.toLowerCase();
        
        // If we're editing and have a deployment, filter by company type
        if (deployment && deployment.company) {
            const currentCompanyType = deployment.company.companyType;
            if (company.companyType !== currentCompanyType) {
                return false;
            }
        }
        
        return companyName.toLowerCase().includes(searchTerm) || companyId.toLowerCase().includes(searchTerm);
    });

    const handleSelectCompany = (company: Company) => {
        setSelectedCompany(company);
        setShowCompanyModal(false);
        setCompanySearchTerm('');
    };

    const handleUpdate = async () => {
        if (!selectedCompany || !deployment || !bankId || !uid) {
            showToastMessage('Please select a company', 'error');
            return;
        }

        setSaving(true);
        try {
            const result = await updateDeploymentData(uid, isBankUser, bankId, deployment.id, selectedCompany.id);
            
            if (!result.success && result.conflict) {
                setConflictData(result.conflict);
                setShowConflictModal(true);
            } else {
                showToastMessage('✅ Deployment updated successfully!', 'success');
                setTimeout(() => {
                    onSuccess();
                    onClose();
                }, 1000);
            }
        } catch (err: any) {
            console.error('Error updating deployment:', err);
            showToastMessage('❌ Failed to update deployment', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleConflictResolve = async (shouldReplace: boolean) => {
        if (!selectedCompany || !deployment || !bankId || !uid || !conflictData) {
            return;
        }

        if (shouldReplace) {
            setSaving(true);
            try {
                await replaceDeploymentData(uid, isBankUser, bankId, deployment.id, selectedCompany.id, conflictData.existingDeployment.id);
                showToastMessage('✅ Deployment updated successfully!', 'success');
                setTimeout(() => {
                    onSuccess();
                    onClose();
                }, 1000);
            } catch (err: any) {
                console.error('Error replacing deployment:', err);
                showToastMessage('❌ Failed to replace deployment', 'error');
            } finally {
                setSaving(false);
            }
        } else {
            setShowConflictModal(false);
            setConflictData(null);
        }
    };

    if (!deployment) {
        return null;
    }

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title="Edit Deployment"
                size="lg"
            >
                <div className="space-y-6">
                    {/* Branch Info (Read-only) */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Branch
                        </label>
                        <TextField
                            type="text"
                            value={deployment.branch ? `${deployment.branch.branchCode} - ${deployment.branch.address}` : 'N/A'}
                            readOnly
                            className="bg-gray-50 dark:bg-gray-800"
                        />
                    </div>

                    {/* Current Company Info (Read-only) */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Current Company
                        </label>
                        <TextField
                            type="text"
                            value={deployment.company ? deployment.company.companyName : 'No Company Assigned'}
                            readOnly
                            className="bg-gray-50 dark:bg-gray-800"
                        />
                    </div>

                    {/* New Company Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Select New Company *
                        </label>
                        {deployment.company && (
                            <div className="mb-2 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20">
                                <p className="text-xs text-blue-800 dark:text-blue-200">
                                    <strong>Note:</strong> Showing only {deployment.company.companyType} companies to maintain the same service type.
                                </p>
                            </div>
                        )}
                        <div className="flex gap-2">
                            <TextField
                                type="text"
                                placeholder={selectedCompany ? selectedCompany.companyName : "Click to select company"}
                                value=""
                                readOnly
                                className="bg-gray-50 dark:bg-gray-800"
                            />
                            <Button
                                variant="secondary"
                                onClick={() => setShowCompanyModal(true)}
                            >
                                Select
                            </Button>
                        </div>
                    </div>

                    {/* Selected Company Info */}
                    {selectedCompany && (
                        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
                            <h6 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">Selected Company:</h6>
                            <div className="text-sm text-blue-800 dark:text-blue-200">
                                <strong>Company:</strong> {selectedCompany.companyName} ({selectedCompany.companyType})
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <Button variant="secondary" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleUpdate}
                            disabled={!selectedCompany || saving}
                        >
                            {saving ? 'Updating...' : 'Update Deployment'}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Company Selection Modal */}
            <Modal
                isOpen={showCompanyModal}
                onClose={() => setShowCompanyModal(false)}
                title={`Select ${deployment.company?.companyType || 'Company'}`}
                size="xl"
            >
                <div className="space-y-4">
                    {deployment.company && (
                        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20">
                            <p className="text-xs text-blue-800 dark:text-blue-200">
                                <strong>Note:</strong> Showing only {deployment.company.companyType} companies to maintain the same service type.
                            </p>
                        </div>
                    )}
                    <div className="relative">
                        <Icon icon={FiSearch} className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by company name or ID..."
                            value={companySearchTerm}
                            onChange={(e) => setCompanySearchTerm(e.target.value)}
                            className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                        />
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="border-b-2 border-gray-300 bg-gray-50 dark:bg-gray-800">
                                    <th className="border-r border-gray-200 px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:border-gray-600 dark:text-gray-300">
                                        Company Name
                                    </th>
                                    <th className="border-r border-gray-200 px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:border-gray-600 dark:text-gray-300">
                                        Type
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                                        Company ID
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                                        Action
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredCompanies.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                                            No companies found
                                        </td>
                                    </tr>
                                ) : (
                                    filteredCompanies.map((company) => (
                                        <tr key={company.id} className="border-b border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800/50">
                                            <td className="border-r border-gray-200 px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100" style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                                <strong>{company.companyName}</strong>
                                            </td>
                                            <td className="border-r border-gray-200 px-4 py-3 dark:border-gray-700">
                                                <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                                                    {company.companyType || 'N/A'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                                {company.companyId || 'N/A'}
                                            </td>
                                            <td className="px-4 py-3">
                                                <Button
                                                    onClick={() => handleSelectCompany(company)}
                                                    className="px-3 py-1.5 text-xs"
                                                >
                                                    Select
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </Modal>

            {/* Conflict Resolution Modal */}
            <Modal
                isOpen={showConflictModal}
                onClose={() => handleConflictResolve(false)}
                title="Deployment Conflict"
                size="md"
            >
                <div className="space-y-4">
                    <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20">
                        <div className="flex items-center gap-2">
                            <Icon icon={FiAlertTriangle} className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                            <strong className="text-yellow-900 dark:text-yellow-100">Warning:</strong>
                        </div>
                        <p className="mt-2 text-sm text-yellow-800 dark:text-yellow-200">
                            Branch already has a company of the same type deployed.
                        </p>
                    </div>

                    <div>
                        <strong className="text-gray-900 dark:text-gray-100">Current Company:</strong>
                        <div className="mt-1 ml-3 text-sm text-gray-600 dark:text-gray-400">
                            <strong>Company:</strong> {conflictData?.existingCompany?.companyName} ({conflictData?.existingCompany?.companyType})
                        </div>
                    </div>

                    <div>
                        <strong className="text-gray-900 dark:text-gray-100">New Company:</strong>
                        <div className="mt-1 ml-3 text-sm text-gray-600 dark:text-gray-400">
                            <strong>Company:</strong> {selectedCompany?.companyName} ({selectedCompany?.companyType})
                        </div>
                    </div>

                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        If you proceed, the existing company will be replaced.
                    </p>

                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <Button variant="secondary" onClick={() => handleConflictResolve(false)}>
                            Cancel
                        </Button>
                        <Button 
                            variant="primary" 
                            onClick={() => handleConflictResolve(true)} 
                            disabled={saving}
                            className="bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500"
                        >
                            {saving ? 'Replacing...' : 'Replace Existing Company'}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Toast */}
            {showToast && (
                <div className="pointer-events-none fixed right-4 top-4 z-50">
                    <div className="pointer-events-auto">
                        <Toast
                            message={successMessage}
                            type={successMessage.includes('❌') ? 'error' : 'success'}
                            onClose={() => setShowToast(false)}
                        />
                    </div>
                </div>
            )}
        </>
    );
}

export default EditDeploymentModal;

